import { CustomGchatInstructions } from "@/components/CustomGchatInstructions";
import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, type ContainerRow, type Permissions } from "@/lib/api";
import { booChatStatus, type ConnectionStatus } from "@/lib/connection-status";
import { useFeatureFlag } from "@/lib/feature-flag";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { BooAppActions, BooAppPanel } from "./BooApp";
import {
  ConnectionHeader,
  ConnectionSection,
  DetailList,
  DetailRow,
  DisconnectButton,
  Notice,
  StatusLine,
} from "./ConnectionParts";
import type { ConnectionDef } from "./connections-lib";
import { useBooApp } from "./use-boo-app";

// Google Chat: the Boo Chat app (only for boolab.ai members until the
// Marketplace listing clears Google's review) or the org's own Chat app in its
// Google Cloud project. With its own app, Boo can also read channel history
// through an OAuth client in the same project.
export function GoogleChatConnection({
  def,
  container,
  permissions,
}: {
  def: ConnectionDef;
  container: ContainerRow;
  permissions: Permissions;
}) {
  const booAppEnabled = useFeatureFlag("gchat-boo-app");
  const app = useBooApp("gchat", container, { enabled: booAppEnabled });
  const [setupOpen, setSetupOpen] = useState(false);
  const canConnect = permissions?.canConnectGchat !== false;
  const ownQuery = useQuery({
    queryKey: ["gchat-custom-status", container.id],
    queryFn: () => api.gchat.custom.status(container.id),
    enabled: container.deploymentStatus === "running",
  });
  const own = ownQuery.data;
  const ownConnected = own?.connected === true;
  const ownPlace = own?.customerName ?? own?.customerId ?? "your Google Workspace";

  const status: ConnectionStatus = ownConnected
    ? { loading: false, state: "connected", label: `Your own app in ${ownPlace}` }
    : booAppEnabled
      ? booChatStatus(app.data, app.query.isLoading, app.place, container.id)
      : { loading: ownQuery.isLoading, state: "off", label: "Not connected" };

  const header = (
    <ConnectionHeader
      def={def}
      status={<StatusLine status={status} className="h-5 text-sm" />}
      actions={booAppEnabled && <BooAppActions app={app} container={container} />}
    />
  );

  if (booAppEnabled && (app.query.isLoading || app.query.isError || !app.data)) {
    return (
      <>
        {header}
        {app.query.isLoading ? (
          <LoadingIndicator message="Checking Google Chat…" />
        ) : (
          <InlineError error={app.query.error ?? "Failed to check Google Chat status"} />
        )}
      </>
    );
  }

  return (
    <>
      {header}
      {booAppEnabled && (
        <ConnectionSection
          title="Boo Chat app"
          description="Boo answers in Google Chat as Boo. A Google Workspace talks through one Boo at a time."
        >
          <BooAppPanel
            app={app}
            usage="DM it, or @mention it in any Space where it's added."
            pitch="One-click install. Anyone in your workspace can then DM Boo or @mention it."
          />
        </ConnectionSection>
      )}

      <ConnectionSection
        title="Your own Chat app"
        description="A Chat app in your own Google Cloud project, under your name and icon."
      >
        {ownConnected ? (
          <DetailList>
            <DetailRow
              term="Receiving from"
              action={
                canConnect && (
                  <Button size="sm" variant="outline" onClick={() => setSetupOpen(true)}>
                    Manage
                  </Button>
                )
              }
            >
              {ownPlace}
            </DetailRow>
            {own?.eventsUrl && (
              <DetailRow term="Events URL">
                <span className="font-mono text-xs break-all">{own.eventsUrl}</span>
              </DetailRow>
            )}
          </DetailList>
        ) : (
          canConnect && (
            <Button
              size="sm"
              variant={booAppEnabled ? "outline" : "default"}
              className="w-fit"
              onClick={() => setSetupOpen(true)}
            >
              Set up your own app
            </Button>
          )
        )}
      </ConnectionSection>

      {ownConnected && <ChannelHistory containerId={container.id} canManage={canConnect} />}

      <Sheet open={setupOpen} onOpenChange={setSetupOpen}>
        <SheetContent side="right" className="data-[side=right]:sm:max-w-2xl overflow-y-auto">
          <SheetHeader className="border-b">
            <SheetTitle>Set up your own Chat app</SheetTitle>
            <SheetDescription>
              Your bot, your branding, in your own Google Cloud project.
            </SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-6">
            <CustomGchatInstructions container={container} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// By default Boo sees only messages sent to it; an OAuth client in the same
// Google Cloud project lets it read full channel history.
function ChannelHistory({ containerId, canManage }: { containerId: string; canManage: boolean }) {
  const queryClient = useQueryClient();
  const key = ["gchat-custom-user-oauth-status", containerId];
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const statusQuery = useQuery({ queryKey: key, queryFn: () => api.gchat.custom.userOauth.status(containerId) });
  const saveClient = useMutation({
    mutationFn: () => api.gchat.custom.userOauth.saveClient(containerId, clientId.trim(), clientSecret.trim()),
    onSuccess: () => {
      setClientSecret("");
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
  const disconnect = useMutation({
    mutationFn: () => api.gchat.custom.userOauth.disconnect(containerId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const data = statusQuery.data;
  if (statusQuery.isLoading || !data) return null;

  return (
    <ConnectionSection title="Channel history" description="By default Boo only sees messages sent to it.">
      {data.agentOffline ? (
        <Notice>
          <p>Boo is offline. Channel history can be set up once it&apos;s back.</p>
        </Notice>
      ) : data.featureUnavailable ? (
        <Notice>
          <p>Channel history isn&apos;t ready on this Boo just yet.</p>
        </Notice>
      ) : !data.clientConfigured ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            To let Boo read past messages, create an OAuth 2.0 Web Client in the same Google Cloud project as your
            Chat app and paste its credentials here.
            {data.redirectUri && (
              <>
                {" "}
                Add this URL to its <em>Authorized redirect URIs</em>:{" "}
                <span className="font-mono text-xs break-all">{data.redirectUri}</span>
              </>
            )}
          </p>
          {canManage && (
            <form
              className="space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                saveClient.mutate();
              }}
            >
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor={`gchat-oauth-client-id-${containerId}`} className="text-xs">
                    OAuth client ID
                  </Label>
                  <Input
                    id={`gchat-oauth-client-id-${containerId}`}
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="123-abc.apps.googleusercontent.com"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`gchat-oauth-client-secret-${containerId}`} className="text-xs">
                    OAuth client secret
                  </Label>
                  <Input
                    id={`gchat-oauth-client-secret-${containerId}`}
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder="GOCSPX-..."
                  />
                </div>
              </div>
              <Button type="submit" size="sm" disabled={!clientId.trim() || !clientSecret.trim() || saveClient.isPending}>
                {saveClient.isPending && <Loader2 className="size-3.5 animate-spin" />}
                Save client
              </Button>
              {saveClient.isError && <InlineError error={saveClient.error} />}
            </form>
          )}
        </div>
      ) : (
        <Notice
          tone={data.connected ? "neutral" : "warning"}
          action={
            canManage && (
              <>
                {!data.connected && data.installUrl && (
                  <Button size="sm" asChild>
                    <a
                      href={`${data.installUrl}?returnTo=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Authorize
                    </a>
                  </Button>
                )}
                <DisconnectButton pending={disconnect.isPending} onClick={() => disconnect.mutate()} />
              </>
            )
          }
        >
          <p className="font-medium">
            {data.connected ? "Boo can read channel history" : "Client saved. Finish authorizing."}
          </p>
          <p className="text-xs text-muted-foreground">
            {data.connected
              ? "It reads past messages in any Space the connecting admin is a member of."
              : "Authorize as the admin whose Spaces Boo should read. It grants chat.messages.readonly."}
          </p>
        </Notice>
      )}
      {disconnect.isError && <InlineError error={disconnect.error} />}
    </ConnectionSection>
  );
}
